import React from 'react';
import { motion } from 'motion/react';
import { ChefHat, Flame, MapPin, Instagram, Phone, Mail, HelpCircle, Utensils } from 'lucide-react';
import CountdownTimer from './components/CountdownTimer';
import NewsletterForm from './components/NewsletterForm';

const App: React.FC = () => {

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 selection:bg-red-200 selection:text-red-900 flex flex-col justify-between">
      
      {/* 🚀 Announcement Top Bar */}
      <div className="bg-gradient-to-r from-red-800 via-[#A8201A] to-green-800 text-white text-center py-2 px-4 shadow-sm relative z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold tracking-wide">
          <span>Exciting News: 505 Foods is under structural construction in Albuquerque! Grand Opening Coming Soon.</span>
        </div>
      </div>

      {/* 👑 Main Header */}
      <header className="border-b border-stone-200/50 bg-white/70 backdrop-blur-md sticky top-0 z-40 transition-all min-h-20 py-3 sm:py-0">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 min-h-20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-700 flex items-center justify-center shadow-md">
              <span className="text-white font-extrabold text-lg tracking-tighter">505</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xl sm:text-2xl font-black text-stone-900 serif tracking-tight">
                505 Foods
              </span>
              <span className="text-[10px] sm:text-xs text-stone-500 tracking-widest font-mono uppercase leading-tight">
                Albuquerque, NM
              </span>
            </div>
          </div>

          <div className="flex items-center justify-center flex-grow mx-4 max-w-xl w-full">
            <span className="inline-flex items-center justify-center gap-2.5 bg-red-50 border-2 border-red-200/85 text-red-800 px-6 py-3 rounded-2xl text-xs sm:text-sm font-black uppercase tracking-widest shadow-md transition-all w-full text-center">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-450 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-650"></span>
              </span>
              Under Construction — Coming Soon to ABQ
            </span>
          </div>

          <div className="flex items-center">
            <a 
              href="#newsletter-form" 
              className="bg-stone-900 hover:bg-stone-800 text-white font-bold text-xs uppercase tracking-widest py-3 px-6 rounded-xl shadow-sm transition-all text-center min-w-[130px]"
            >
              Get Notified
            </a>
          </div>
        </div>
      </header>

      {/* 🌟 Content Frame */}
      <main className="flex-grow space-y-12 sm:space-y-20 pb-16">
        
        {/* 1. Hero / Landing Header */}
        <section className="max-w-5xl mx-auto text-center px-6 pt-12 sm:pt-20 space-y-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-1.5 bg-stone-100 border border-stone-250/50 text-stone-700 rounded-full py-1.5 px-4 text-xs font-semibold uppercase tracking-wider"
          >
            <ChefHat className="w-4 h-4 text-red-700 animate-bounce" />
            Elevation of South-Western Gastronomy
          </motion.div>

          {/* Majestic Typography */}
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-6xl font-black tracking-tight text-stone-900 leading-[1.1] max-w-4xl mx-auto"
          >
            The Soul of New Mexican Taste, <span className="text-red-700 relative inline-block">
              Redefined.
              <span className="absolute left-0 bottom-1.5 w-full h-1 bg-red-700/10 rounded-full" />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-stone-600 text-base sm:text-xl max-w-2xl mx-auto font-medium"
          >
            From heritage red chile pods to local Hatch green harvest, 505 Foods blends traditional southwest recipes with contemporary culinary design.
          </motion.p>

          {/* Launch countdown component */}
          <CountdownTimer />
        </section>

        {/* 2. Interactive Modules Side-by-Side (Waitlist & Visual Concept) */}
        <section className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-8 items-center" id="coming-soon-panel">
          
          {/* Form subscriber column */}
          <div className="md:col-span-6 order-2 md:order-1">
            <NewsletterForm />
          </div>

          {/* Descriptive Pitch column */}
          <div className="md:col-span-6 space-y-6 order-1 md:order-2">
            <span className="block text-xs font-bold text-red-700 uppercase tracking-widest font-mono">
              The Conception
            </span>
            <div className="space-y-4">
              <h2 className="text-3xl font-extrabold text-stone-900 serif leading-tight">
                Authentic Albuquerque Heritage Meets Contemporary Craftsmanship
              </h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Currently under construction in downtown Albuquerque, <strong>505 Foods</strong> is a long-standing culinary dream brought to life by local chefs and food artisans. We are fully committed to highlighting local agriculture: our organic blue corn is ground in-house, and our red and green chile pods are harvested exclusively from farms in Hatch and Chimayó.
              </p>
            </div>

            {/* Quick feature grid items */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-stone-200/60 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-1.5 text-red-700 mb-1.5">
                  <Flame className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-wide">100% Native Heat</span>
                </div>
                <p className="text-stone-500 text-[11px] leading-tight">
                  Hand-selected chiles, fire-roasted or ground in house for pristine spice flavor profiles.
                </p>
              </div>

              <div className="bg-white border border-stone-200/60 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-1.5 text-green-700 mb-1.5">
                  <MapPin className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-wide">ABQ Central Hub</span>
                </div>
                <p className="text-stone-500 text-[11px] leading-tight">
                  A beautiful visual community sanctuary featuring a local salsa garden & patio.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Frequently Asked Questions (Premium Visuals) */}
        <section className="max-w-4xl mx-auto px-6 border-t border-stone-200 pt-16 space-y-8" id="faqs">
          <div className="text-center space-y-2">
            <HelpCircle className="w-8 h-8 text-amber-600 mx-auto animate-pulse" />
            <h3 className="text-2xl font-bold tracking-tight text-stone-900 serif">
              Frequently Anticipated Questions
            </h3>
            <p className="text-sm text-stone-600">
              Curious about what 505 Foods is baking? Let's shed some light.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm space-y-2">
              <h4 className="text-sm font-extrabold text-stone-900 flex items-center gap-1.5">
                <Utensils className="w-4 h-4 text-red-700 flex-shrink-0" />
                Where will 505 Foods be located?
              </h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                We are building our beautiful cornerstone location in the historical downtown core of Albuquerque, New Mexico. The sanctuary features high-desert outdoor seating and a warm, spacious community table interior.
              </p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm space-y-2">
              <h4 className="text-sm font-extrabold text-stone-900 flex items-center gap-1.5">
                <Flame className="w-4 h-4 text-green-700 flex-shrink-0" />
                How hot is the spice level going to be?
              </h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                We respect native authentic New Mexican heat! We offer everything from mild calabacita creations to fiery hand-roasted autumn-crop green chiles. You can customize the spice profile of almost every dish to fit your comfort zone.
              </p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm space-y-2">
              <h4 className="text-sm font-extrabold text-stone-900 flex items-center gap-1.5">
                How can I get a pre-opening ticket?
              </h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                Simple! Submit your email in the VIP Waitlist form above. We are capping our pre-opening private dining tastings at 250 local members, so submit early to secure your membership invitation code!
              </p>
            </div>
          </div>
        </section>

      </main>

      {/* 🍂 Footer */}
      <footer className="border-t border-stone-200 bg-stone-900 text-stone-100 py-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          
          <div className="md:col-span-7 space-y-3">
            <div className="flex items-center gap-2">
              <div className="bg-red-800 text-white w-8 h-8 rounded-lg flex items-center justify-center font-extrabold text-sm shadow">505</div>
              <span className="text-lg font-bold uppercase tracking-wider serif text-white">505 Foods</span>
            </div>
            <p className="text-stone-400 text-xs leading-relaxed max-w-sm">
              An upcoming flagship culinary oasis in downtown Albuquerque, celebrating the absolute soul of New Mexican farmers, traditional slow-braised marinades, and high-desert culinary modernism.
            </p>
          </div>

          <div className="md:col-span-5 space-y-3 md:text-right">
            <span className="block text-stone-100 text-xs font-bold uppercase tracking-wider">Downtown ABQ Community</span>
            <div className="flex gap-4 md:justify-end text-stone-400">
              <a href="https://instagram.com" referrerPolicy="no-referrer" target="_blank" className="hover:text-red-400 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="mailto:info@505foods.com" className="hover:text-red-400 transition-colors">
                <Mail className="w-5 h-5" />
              </a>
              <a href="tel:+15058211111" className="hover:text-red-400 transition-colors">
                <Phone className="w-5 h-5" />
              </a>
            </div>
            <p className="text-[10px] text-stone-500 leading-tight">
              Copyright &copy; 2026 505 Foods Inc. All Rights Saved. &bull; Designed by{' '}
              <a
                href="https://klikful.com"
                target="_blank"
                rel="noopener"
                className="text-stone-400 hover:text-white underline underline-offset-2 transition-colors"
              >
                klikful.com
              </a>
            </p>
          </div>

        </div>
      </footer>
    </div>
  );
};

export default App;
